/**
 * Transactions: the restaurant's card payments (Aster Gateway) and the cash /
 * manual tenders the register recorded - GET /api/merchant/transactions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DollarSign, CreditCard, Receipt, Search, Download, RefreshCw, Loader2, Banknote, Gift, Smartphone, Coins, Star,
  ChevronLeft, ChevronRight, ChevronDown, SplitSquareHorizontal,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

import { API } from '@/config/api';
import { formatDateTime, formatTime } from '@/lib/restaurantTime';

const METHODS = {
  card: { icon: CreditCard, color: 'text-blue-600', label: 'Card' },
  cash: { icon: Banknote, color: 'text-green-600', label: 'Cash' },
  zelle: { icon: Smartphone, color: 'text-violet-600', label: 'Zelle' },
  venmo: { icon: Smartphone, color: 'text-sky-600', label: 'Venmo' },
  crypto: { icon: Coins, color: 'text-orange-600', label: 'Crypto' },
  gift_card: { icon: Gift, color: 'text-pink-600', label: 'Gift Card' },
  loyalty: { icon: Star, color: 'text-amber-600', label: 'Loyalty' },
};

const STATUS = {
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-800' },
  timed_out: { label: 'Timed out', className: 'bg-orange-100 text-orange-800' },
  error: { label: 'Not completed', className: 'bg-red-100 text-red-800' },
  open: { label: 'In progress', className: 'bg-yellow-100 text-yellow-800' },
};
// what a row shows (the server's display_status): the money that went back, or the split order's state
const SHOWN = {
  ...STATUS,
  paid: { label: 'Paid', className: 'bg-green-100 text-green-800' },
  balance_due: { label: 'Balance due', className: 'bg-yellow-100 text-yellow-800' },
  partially_refunded: { label: 'Partly refunded', className: 'bg-amber-100 text-amber-800' },
  refunded: { label: 'Refunded', className: 'bg-red-100 text-red-800' },
  voided: { label: 'Voided', className: 'bg-gray-200 text-gray-800' },
};
const shown = (key) => SHOWN[key] || { label: String(key || 'Unknown').replace(/_/g, ' '), className: 'bg-gray-100 text-gray-800' };

const KIND = { sale: 'Sale', void: 'Void', refund: 'Refund', tip_adjust: 'Tip adjust' };
const SOURCE = { pos: 'Register', kiosk: 'Kiosk', online: 'Online' };
const PAGE_SIZE = 50;

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const isoDay = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

export default function TransactionsPage({ merchantToken, restaurantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 6); return isoDay(d); });
  const [dateTo, setDateTo] = useState(() => isoDay(new Date()));
  const [method, setMethod] = useState('all');
  const [status, setStatus] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);
  const zone = data?.timezone; // the restaurant's own clock (restaurants.timezone), never the browser's

  const headers = useMemo(() => ({
    ...(merchantToken ? { Authorization: `Bearer ${merchantToken}` } : {}),
    ...(restaurantId ? { 'X-Merchant-ID': String(restaurantId) } : {}),
  }), [merchantToken, restaurantId]);

  const query = useCallback((extra = {}) => {
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, method, status, ...extra });
    const order = orderSearch.replace(/[^0-9]/g, '');
    if (order) params.set('order_id', order);
    return params;
  }, [dateFrom, dateTo, method, status, orderSearch]);

  const load = useCallback(async () => {
    if (!merchantToken) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/merchant/transactions?${query({ page: String(page), page_size: String(PAGE_SIZE) })}`, { headers });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Transactions are unavailable right now');
      setData(body);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [merchantToken, headers, query, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, method, status, orderSearch]);

  const exportCSV = async () => {
    // Every row of the current filter, page by page.
    try {
      const rows = [];
      for (let p = 1; p <= 50; p += 1) {
        const res = await fetch(`${API}/merchant/transactions?${query({ page: String(p), page_size: '200' })}`, { headers });
        const body = await res.json();
        if (!res.ok) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Export failed');
        rows.push(...body.transactions);
        if (rows.length >= body.total || body.transactions.length === 0) break;
      }
      if (rows.length === 0) { toast.error('No transactions to export'); return; }
      const head = ['Date', 'Order', 'Method', 'Type', 'Status', 'Amount', 'Tip', 'Total', 'Card', 'Auth code', 'Reference'];
      const csv = [head, ...rows.map(t => [
        formatDateTime(t.created_at, zone), t.order_id || '', t.split ? t.split.text : ((METHODS[t.method] || {}).label || t.method),
        KIND[t.kind] || t.kind, shown(t.display_status || t.status).label,
        (t.amount_cents / 100).toFixed(2), (t.tip_cents / 100).toFixed(2), (t.total_cents / 100).toFixed(2),
        t.last4 ? `${t.card_brand || ''} ${t.last4}`.trim() : '', t.auth_code || '', t.reference || '',
      ])].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${dateFrom}_${dateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Transactions exported');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const transactions = data?.transactions || [];
  const summary = data?.summary || {};
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Receipt className="h-6 w-6" /> Transactions</h2>
          <p className="text-muted-foreground">Card payments through Aster Gateway and payments recorded at the register</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={loading}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">From:</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">To:</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
            </div>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All methods" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                {Object.entries(METHODS).map(([key, m]) => <SelectItem key={key} value={key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS).map(([key, s]) => <SelectItem key={key} value={key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex-1 min-w-48 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Order number" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} className="pl-10" />
            </div>
            <Button variant="outline" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4">
        <p className="text-xs text-muted-foreground mb-2">Money taken in this date range</p>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
          {Object.entries(METHODS).map(([key, { icon: Icon, color, label }]) => (
            <div key={key} className="text-center">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground">
                <Icon className={`h-4 w-4 ${color}`} /> {label}
              </div>
              <p className={`text-lg font-bold ${color}`}>{money(summary[key]?.total_cents)}</p>
              <p className="text-[11px] text-muted-foreground">{summary[key]?.count || 0} payment(s)</p>
            </div>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="min-w-[860px]">
            <div className="grid grid-cols-9 gap-2 px-4 py-3 bg-muted/50 text-xs font-medium text-muted-foreground uppercase border-b">
              <div>Date</div><div>Order</div><div>Method</div><div>Type</div><div>Amount</div><div>Tip</div><div>Total</div><div>Card</div><div>Status</div>
            </div>
            <div className="divide-y">
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : error ? (
                <p className="p-6 text-sm text-red-600" data-testid="transactions-error">{error}</p>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No transactions found</p>
                  <p className="text-sm">Try another date range or filter</p>
                </div>
              ) : transactions.map((t) => (t.split ? (
                <div key={t.id} data-testid={`transaction-split-${t.order_id}`}>
                  <button type="button" onClick={() => setOpenRow(openRow === t.id ? null : t.id)} aria-expanded={openRow === t.id}
                    className="w-full grid grid-cols-9 gap-2 px-4 py-3 items-center hover:bg-muted/50 text-sm text-left">
                    <div className="text-xs">
                      {formatDateTime(t.created_at, zone, { hour: undefined, minute: undefined })}<br />
                      <span className="text-muted-foreground">{formatTime(t.created_at, zone)}</span>
                    </div>
                    <div>#{t.order_id}</div>
                    <div className="col-span-2 flex items-center gap-2">
                      <SplitSquareHorizontal className="h-4 w-4 text-sky-600" />
                      <Badge className="bg-sky-100 text-sky-800" data-testid="transaction-split-badge">{t.split.badge}</Badge>
                    </div>
                    <div>{money(t.amount_cents)}</div>
                    <div className="text-green-600">{money(t.tip_cents)}</div>
                    <div className="font-bold">{money(t.total_cents)}</div>
                    <div className="text-xs text-muted-foreground truncate" title={t.split.text}>{t.split.parts.filter(p => p.status !== 'voided').map(p => p.label).join(', ')}</div>
                    <div className="flex items-center gap-1">
                      <Badge className={shown(t.display_status || t.status).className} data-testid="transaction-split-status">{shown(t.display_status || t.status).label}</Badge>
                      <ChevronDown className={`h-4 w-4 transition-transform ${openRow === t.id ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {openRow === t.id && (
                    <div className="bg-muted/30 px-4 pb-3" data-testid="transaction-split-detail">
                      <p className="text-xs text-muted-foreground py-2">{t.split.text} · order total {money(t.order_total_cents)}{t.fee_cents ? ` · card fees ${money(t.fee_cents)}` : ''}</p>
                      <div className="grid grid-cols-7 gap-2 text-[11px] uppercase text-muted-foreground font-medium pb-1">
                        <div>Payment</div><div>Amount</div><div>Tip</div><div>Fee</div><div>Time</div><div>Status</div><div>Refunded</div>
                      </div>
                      {t.split.parts.map((p, i) => (
                        <div key={`${p.kind}-${p.payment_id || p.tender_id || i}`} className="grid grid-cols-7 gap-2 py-1.5 text-sm border-t border-border/50" data-testid={`transaction-split-part-${i}`}>
                          <div className="font-medium">{p.label}</div>
                          <div>{money(p.amount_cents)}</div>
                          <div className="text-green-600">{money(p.tip_cents)}</div>
                          <div>{money(p.fee_cents)}</div>
                          <div className="text-xs">{p.created_at ? formatTime(p.created_at, zone) : '—'}</div>
                          <div><Badge className={shown(p.display_status || p.status).className}>{shown(p.display_status || p.status).label}</Badge></div>
                          <div className={p.refunded_cents ? 'text-red-600' : 'text-muted-foreground'}>{p.refunded_cents ? money(p.refunded_cents) : '—'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (() => {
                const m = METHODS[t.method] || { icon: Receipt, color: 'text-slate-500', label: t.method };
                const s = shown(t.display_status || t.status);
                return (
                  <div key={t.id} className="grid grid-cols-9 gap-2 px-4 py-3 items-center hover:bg-muted/50 text-sm">
                    <div className="text-xs">
                      {formatDateTime(t.created_at, zone, { hour: undefined, minute: undefined })}<br />
                      <span className="text-muted-foreground">{formatTime(t.created_at, zone)}</span>
                    </div>
                    <div>{t.order_id ? `#${t.order_id}` : '—'}</div>
                    <div className="flex items-center gap-2"><m.icon className={`h-4 w-4 ${m.color}`} /><span>{m.label}</span></div>
                    <div className="text-xs">{KIND[t.kind] || t.kind}<br /><span className="text-muted-foreground">{t.terminal || SOURCE[t.source] || ''}</span></div>
                    <div>{money(t.amount_cents)}</div>
                    <div className="text-green-600">{money(t.tip_cents)}</div>
                    <div className="font-bold">{money(t.total_cents)}</div>
                    <div className="text-xs">
                      {t.last4 ? <><span className="uppercase">{t.card_brand}</span> ••••{t.last4}</> : '—'}
                      {t.auth_code && <div className="text-muted-foreground">Auth {t.auth_code}</div>}
                    </div>
                    <div title={t.message || ''}><Badge className={s.className} data-testid="transaction-status">{s.label}</Badge></div>
                  </div>
                );
              })()))}
            </div>
          </div>
        </CardContent>
      </Card>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <span className="text-muted-foreground">Page {page} of {pages} · {data.total} transactions</span>
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page >= pages || loading} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
